import type { ReactNode } from "react"
import { useState } from "react"

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical } from "@/components/icons"

import type { DashboardSpec } from "../dashboard/catalog"

import { cn } from "@/lib/utils"

type SortableDashboardProps = {
  spec: DashboardSpec
  onSpecChange?: (spec: DashboardSpec) => void
  canEdit?: boolean
  disabled?: boolean
  children: ReactNode
  renderOverlay?: (activeIndex: number) => ReactNode
}

export function SortableDashboard({
  spec,
  onSpecChange,
  canEdit,
  disabled,
  children,
  renderOverlay,
}: SortableDashboardProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const items = spec.children.map((_, i) => `child-${i}`)

  if (!canEdit || !onSpecChange) return <>{children}</>

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id as string
    setActiveIndex(Number(id.replace("child-", "")))
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveIndex(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = Number((active.id as string).replace("child-", ""))
    const newIndex = Number((over.id as string).replace("child-", ""))

    const newChildren = arrayMove(spec.children, oldIndex, newIndex)
    onSpecChange!({ ...spec, children: newChildren })
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext disabled={disabled} items={items} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
      <DragOverlay>
        {activeIndex !== null && renderOverlay ? (
          <div className="rounded-lg opacity-80 shadow-lg">{renderOverlay(activeIndex)}</div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

type SortableItemProps = {
  id: string
  children: ReactNode
}

export function SortableItem({ id, children }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Drag affordance redesigned after a user reported "tiles can't be
  // moved any longer". The previous chip sat above the tile (-top-2),
  // was 26x18px, and only became visible on hover via opacity-0 →
  // opacity-100. Two problems made it feel broken:
  //   - Hit target was tiny and easy to miss
  //   - Hover-only visibility meant new users never discovered it
  // Replaced with a left-edge gutter handle that's always visible at
  // 35% opacity (subtle but discoverable) and snaps to 100% on hover.
  // Sits OUTSIDE the tile's left edge so it never clips, and gives a
  // generous 16x40 hit target.
  return (
    <div
      ref={setNodeRef}
      className={cn("group/sortable relative", isDragging && "z-50 opacity-50")}
      style={style}
      {...attributes}
    >
      <div
        aria-label="Drag to reorder"
        className={cn(
          "absolute top-1/2 -left-5 z-10 flex h-10 w-4 -translate-y-1/2",
          "cursor-grab items-center justify-center rounded-md",
          "text-muted-foreground/60 hover:text-foreground hover:bg-muted/60",
          "opacity-40 transition-opacity group-hover/sortable:opacity-100",
          "active:cursor-grabbing"
        )}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </div>
      {children}
    </div>
  )
}
